/**
 * The keyword search surface — form, filters, results, paging — as ONE
 * component with ONE implementation.
 *
 * WHY THIS EXISTS. A reduced keyword copy once lived in a separate chat
 * workbench and it cost exactly what a second implementation always costs:
 * no proximity groups, no synonyms, no section scope, no exclude terms, no
 * per-drug grouping (a 7-match drug rendered as seven identical rows), and no
 * way to open the drug you just found. The workbench is gone; this is now the
 * one keyword implementation, rendered by SearchPage inside a shell tab.
 *
 * WHAT STAYED BEHIND. URL reading and writing, and sessionStorage. SearchPage
 * owns a ~170-line effect that reverse-syncs `?kwConfig` / `?kwPage` /
 * `?kwSearchMode` into state, with a documented history of back-nav and
 * replay-nonce bugs. Moving it would have meant rewriting it; instead it keeps
 * its exact logic and pushes through `KeywordSurfaceHandle` (see below).
 *
 * WHY AN IMPERATIVE HANDLE AND NOT PROPS. The obvious controlled-component
 * shape — host owns the state, surface renders it — feeds back on itself
 * here: a search writes the URL, the URL write re-runs the sync effect, and
 * the effect resets the state the search just set. The handle keeps the state
 * in one place (here) and lets the URL restore *push* into it, which is what
 * the effect was always doing.
 */
import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import { useInRouterContext, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { DrugSort } from '../utils/drugResultsTable';
import type {
  ChipKind, Corpus, KeywordSearchGroup, KeywordSearchRequest, KeywordSearchSource,
} from '../types';
import KeywordFilterBar, { KeywordFilters } from './KeywordFilterBar';
import KeywordSearchForm from './KeywordSearchForm';
import KeywordSearchResults from './KeywordSearchResults';
import LetterResultsTable from './LetterResultsTable';
import SearchScopeDropdown from './SearchScopeDropdown';
import { useKeywordSearchQuery, useKeywordSearchSections } from '../hooks/useKeywordSearch';
import { enabledKeywordDocSources } from '../constants/filterConstants';
import type { KeywordDocSource } from '../constants/filterConstants';
import UnlinkedDocumentsSection from './UnlinkedDocumentsSection';
import { authApi } from '../services/authApi';
import type { KeywordSearchResponse } from '../types';
import { isSearchSuggestBarEnabled } from '../config/featureFlags';
import { KW_VIEW_PARAMS, serializeSortParam } from '../utils/keywordViewParams';
import { keywordSearchTerms, proximityOperands } from '../utils/keywordQueryText';
import {
  KW_PAGE_SIZE, applyKwDocSources, buildKwRequest, formatKeywordSearchError, isSingleExactPhrase,
  dropIneligibleApprovalLetter, kwRequestNeedsAdmin, kwSearchIdentity,
} from '../pages/keywordRequest';

/** KeywordSearchResults mirrors its Refine chips / grouping into the URL so
 *  Back from Compare restores them. Spec §4.2 still resets that state when
 *  the table is REPLACED by another view (chip results, or a letters response), so
 *  this drops the params while mounted — otherwise the next Keyword table
 *  would read the stale chips back from the URL. Rendered only inside a
 *  router (the surface itself does not require one). */
function ClearKwViewParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasViewParams = KW_VIEW_PARAMS.some((k) => searchParams.has(k));
  useEffect(() => {
    if (!hasViewParams) return;
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      for (const k of KW_VIEW_PARAMS) params.delete(k);
      return params;
    }, { replace: true });
  }, [hasViewParams, setSearchParams]);
  return null;
}

/** What the last executed search was made of. Paging replays it. */
export interface KwSearchParams {
  groups: KeywordSearchGroup[];
  sources: KeywordSearchSource[];
}

/**
 * The push channel for a host that restores state from somewhere the surface
 * cannot see — today, SearchPage's URL. Each method is the setter the old
 * inline code called, so the restore effect is a rename, not a rewrite.
 */
export interface KeywordSurfaceHandle {
  setRequest(request: KeywordSearchRequest | null): void;
  setPage(page: number): void;
  /** Restore the server sort (SearchPage's URL/Back restore). */
  setSort(sort: DrugSort | null): void;
  setFilters(update: React.SetStateAction<KeywordFilters>): void;
  setLastSearchParams(params: KwSearchParams | null): void;
}

export interface KeywordSurfaceProps {
  /** US-Drugs@FDA visible to this user: the US region reads "US-DailyMed". */
  usFdaVisible?: boolean;
  /** Starting filter state. Read once, at mount. */
  initialFilters: KeywordFilters;
  /** A request restored from a URL / sessionStorage, or null for a cold start. */
  initialRequest?: KeywordSearchRequest | null | undefined;
  initialPage?: number | undefined;
  /** Server sort restored with the request (on-demand mode). Read once, at mount. */
  initialSort?: DrugSort | null | undefined;
  initialLastSearchParams?: KwSearchParams | null | undefined;
  /**
   * Remount key for the FORM, minus the corpus (which is derived here from
   * `filters.searchMode`). KeywordSearchForm reads `initialGroups` /
   * `initialSources` only at mount, so restoring a saved search means
   * remounting it. SearchPage passes `kwConfig:replayNonce`; the workspace
   * passes nothing and the form never remounts.
   */
  formKey?: string | undefined;
  /** Values the form should mount WITH — must be ready at remount time. */
  restoredSearchParams?: KwSearchParams | null | undefined;
  /** Admin origin-label scope. Decides what we ASK for; backend re-checks. */
  versionScope?: 'current' | 'origin' | undefined;
  /**
   * ADMIN ONLY until K4 (spec 2026-10-16 §6a): the "Search in" selection.
   * Passing it is what turns the admin UI on — the multi-select replaces the
   * Document Type pill, every search sends ``document_engine: true`` and the
   * "Unlinked documents" section renders. Undefined (non-admins, the CEPI
   * workspace) = exactly today's surface and request bodies.
   */
  docSources?: KeywordDocSource[] | undefined;
  onDocSourcesChange?: ((next: KeywordDocSource[]) => void) | undefined;
  /**
   * Kept mounted but out of view. SearchPage hides the surface when another
   * tab is showing rather than unmounting it — unmounting would throw away
   * the user's results the moment they glance at Cogni.
   */
  hidden?: boolean | undefined;
  /** Rendered above the form. Gets what it needs to gate on. ``hasResults``
   *  is true once a keyword query has results (or is fetching). */
  header?: ((ctx: { corpus: Corpus; hasResults: boolean }) => React.ReactNode) | undefined;
  /** Rendered below the results — the workspace's [Use in CEPI]. */
  footer?: React.ReactNode | undefined;
  /** Row 0 starts typed as this field kind (legacy ``?kwInput=<kind>`` link). */
  initialRowType?: ChipKind | undefined;
  /** A search ran, or the page changed. The host may persist it. */
  onSearchExecuted?: ((e: {
    request: KeywordSearchRequest;
    page: number;
    filters: KeywordFilters;
    params: KwSearchParams;
    /** true for paging — the host should not push a history entry. */
    replace: boolean;
    /** The server sort in force for this request (null = none). */
    sort: DrugSort | null;
  }) => void) | undefined;
  /**
   * A result set settled. Fires for EVERY page, `page` included — the two
   * hosts want different pages: SearchPage records only page 1 in history,
   * the workspace attaches whichever page is on screen. Gating here would
   * have silently given the workspace page 1's rows for a page-3 attachment.
   */
  onResults?: ((e: {
    data: unknown;
    page: number;
    filters: KeywordFilters;
    params: KwSearchParams;
    request: KeywordSearchRequest;
  }) => void) | undefined;
}

export const KeywordSurface = forwardRef<KeywordSurfaceHandle, KeywordSurfaceProps>(
  function KeywordSurface(props, ref) {
    const {
      initialFilters, initialRequest = null, initialPage = 1, initialSort = null,
      initialLastSearchParams = null, formKey = 'fresh', restoredSearchParams,
      versionScope, hidden = false, header, footer, onSearchExecuted, onResults,
      initialRowType, docSources, onDocSourcesChange, usFdaVisible = false,
    } = props;

    const [filters, setFilters] = useState<KeywordFilters>(initialFilters);
    const [page, setPage] = useState<number>(initialPage);
    const [rawRequest, setRequest] = useState<KeywordSearchRequest | null>(initialRequest);
    // The single source of truth for every consumer (query, Load more,
    // Subscribe, Compare, infinite): a restored/replayed request never carries
    // an approval-letter source this surface would not offer.
    const request = useMemo(
      () => (rawRequest ? dropIneligibleApprovalLetter(rawRequest) : null), [rawRequest]);
    const lastSearchParams = useRef<KwSearchParams | null>(initialLastSearchParams);
    // The filters the EXECUTED search ran with. `filters` is live form state
    // (unsubmitted edits); sort / mode-flip must report these, not that.
    const executedFilters = useRef<KeywordFilters>(initialFilters);
    const snapshotOnNextRender = useRef(false);

    const [serverSort, setServerSort] = useState<DrugSort | null>(initialSort);
    // Bumped on every explicit search and mode-flip restart so Compare ticks
    // clear even when request / page / sort are unchanged.
    const [selectionEpoch, setSelectionEpoch] = useState(0);
    const queryClient = useQueryClient();

    const { data: sectionOptions = [] } = useKeywordSearchSections();
    // Fetch-time opt-in only: the stored request (URL, kw-request, Subscribe)
    // never carries `passages` or `sort`.
    // Non-admin guard (spec 2026-10-16 §6a): without the admin "Search in"
    // (``docSources`` -- set only once the user is SETTLED as an admin) a
    // request asking for the document engine or an admin-only corpus is
    // never sent. Covers a replayed admin URL before the host's coercion
    // lands, and an admin's own URL while /auth/sync is still in flight
    // (it fires the moment the admin flag settles).
    const queryRequest = request && !docSources && kwRequestNeedsAdmin(request)
      ? null : request;
    const query = useKeywordSearchQuery(queryRequest, { passages: 'on_demand', sort: serverSort });

    useImperativeHandle(ref, (): KeywordSurfaceHandle => ({
      setRequest,
      setPage,
      setSort: setServerSort,
      setFilters,
      // A host restore (request + params + pills in one batch): the filters
      // that land with it ARE the executed ones — snapshot after they commit.
      setLastSearchParams: (params) => {
        lastSearchParams.current = params;
        snapshotOnNextRender.current = true;
      },
    }), []);
    useEffect(() => {
      if (!snapshotOnNextRender.current) return;
      snapshotOnNextRender.current = false;
      executedFilters.current = filters;
    });

    // Per-surface corpus. Document Type is bound to `filters.searchMode` by
    // KeywordFilterBar; the derived corpus drives the form's source-locking
    // gate and the remount key.
    // Admin "Search in": a region selection that rules an option out also
    // deselects it (spec §6); the effect keeps the host's state honest.
    const docSel = docSources ? enabledKeywordDocSources(docSources, filters.regions) : undefined;
    const docSelKey = docSel?.join(',');
    useEffect(() => {
      if (docSources && docSelKey !== docSources.join(',')) onDocSourcesChange?.(docSel!);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docSelKey]);
    const docOpts = docSel ? { documentEngine: true } : undefined;

    // Admins keep the drug-shaped Labels corpus whatever the legacy Document
    // Type says: the engine answers letters as drug rows (+ unlinked group).
    const corpus: Corpus = !docSel && filters.searchMode === 'fda_letters' ? 'fda_letters' : 'labels';

    const runSearch = useCallback(
      (groups: KeywordSearchGroup[], formSources: KeywordSearchSource[]) => {
        const sources = docSel
          ? applyKwDocSources(formSources, docSel, versionScope === 'origin') : formSources;
        const params = { groups, sources };
        lastSearchParams.current = params;
        executedFilters.current = filters;
        setPage(1);
        setSelectionEpoch((n) => n + 1);
        const next = buildKwRequest(filters, groups, sources, 0, versionScope, docOpts);
        setRequest(next);
        setServerSort(null);
        onSearchExecuted?.({ request: next, page: 1, filters, params, replace: false, sort: null });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [filters, versionScope, onSearchExecuted, docSelKey],
    );

    const changePage = useCallback(
      (nextPage: number) => {
        const params = lastSearchParams.current;
        if (!params) return;
        setPage(nextPage);
        // The host's original sources can still name an ineligible approval
        // letter; the reported/persisted copy must not (Compare replays it).
        const next = dropIneligibleApprovalLetter(buildKwRequest(
          filters, params.groups, params.sources,
          (nextPage - 1) * KW_PAGE_SIZE, versionScope,
          request?.document_engine ? { documentEngine: true } : undefined,
        ));
        setRequest(next);
        onSearchExecuted?.({
          request: next, page: nextPage, filters, params, replace: true, sort: serverSort,
        });
      },
      [filters, versionScope, onSearchExecuted, serverSort, request],
    );

    const changeSort = useCallback(
      (nextSort: DrugSort) => {
        const params = lastSearchParams.current;
        if (!params || !request) return;
        setServerSort(nextSort);
        setPage(1);
        // Re-sort the EXECUTED search; unsubmitted filter edits must not leak in.
        const next = { ...request, offset: 0 };
        setRequest(next);
        onSearchExecuted?.({
          request: next, page: 1, filters: executedFilters.current, params,
          replace: true, sort: nextSort,
        });
      },
      [request, onSearchExecuted],
    );

    // Mode flip (AC15): the same search answered on-demand and then inline (or
    // vice versa). Drop this search's cached pages FIRST so a stale cached
    // on-demand page 1 cannot bring the flip back, then restart at page 1 with
    // no server sort. The ref is advanced before resetting so this cannot loop.
    const lastMode = useRef<{ id: string; onDemand: boolean } | null>(null);
    useEffect(() => {
      if (!request || query.isFetching || query.data?.kind !== 'drugs') return;
      const id = kwSearchIdentity(request);
      const onDemand = query.data.passages_on_demand === true;
      const prev = lastMode.current;
      lastMode.current = { id, onDemand };
      if (!prev || prev.id !== id || prev.onDemand === onDemand) return;
      queryClient.removeQueries({
        queryKey: ['keyword-search'],
        predicate: (q) => {
          const r = q.queryKey[1] as KeywordSearchRequest | null;
          return !!r && kwSearchIdentity(r) === id;
        },
      });
      setServerSort(null);
      setSelectionEpoch((n) => n + 1);
      const params = lastSearchParams.current;
      // Restart the EXECUTED search at page 1; unsubmitted edits stay out.
      const next = { ...request, offset: 0 };
      if (page !== 1 && params) {
        setPage(1);
        setRequest(next);
      }
      if (params) {
        onSearchExecuted?.({
          request: next, page: 1, filters: executedFilters.current, params,
          replace: true, sort: null,
        });
      }
      // `filters`/`onSearchExecuted`/`versionScope` are read, not tracked.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request, query.isFetching, query.data]);

    // Read through a ref so the effect depends on RESULTS, never on the
    // callback's identity. A host that passes an inline arrow
    // hands over a new function on every render, and
    // listing it as a dependency re-fired this effect on every keystroke —
    // re-reporting results nobody re-ran. Downstream that re-recorded
    // `pending` and, because a new result set DETACHES the attached tab,
    // made [Use in CEPI] undo itself the moment it succeeded (prod,
    // 2026-09-21: attached 11:42:09, detached 11:42:10).
    //
    // The call site is fixed too; this makes the surface safe regardless of
    // what any future host does with it.
    const onResultsRef = useRef(onResults);
    onResultsRef.current = onResults;

    // `hidden` gates this so a background surface stays silent — no
    // Recent-Searches entry for a tab nobody is looking at.
    useEffect(() => {
      if (hidden) return;
      if (!request || query.isFetching || !query.data) return;
      onResultsRef.current?.({
        data: query.data,
        page,
        filters,
        params: lastSearchParams.current ?? {
          groups: request.groups ?? [], sources: request.sources ?? [],
        },
        request,
      });
      // `filters` is read, not tracked: a filter edit alone is not a result.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      // `filters` and `onResults` are read, not tracked.
    }, [hidden, page, request, query.isFetching, query.data]);

    // See ClearKwViewParams: the Keyword results table is replaced by another
    // view, so its URL-mirrored chips / grouping must not outlive it.
    const inRouter = useInRouterContext();
    const tableReplaced = query.data?.kind === 'letters';

    const last = lastSearchParams.current;
    const exact = !!last && isSingleExactPhrase(last.groups);

    const hasResults = !!query.data || query.isFetching;

    const filterBar = (
      <KeywordFilterBar
        filters={filters}
        onFiltersChange={setFilters}
        isLoading={query.isFetching}
        {...(docSel ? {
          docSources: docSel, onDocSourcesChange, originScope: versionScope === 'origin',
        } : {})}
      />
    );

    return (
      <div
        className="space-y-4"
        data-testid="keyword-surface"
        {...(hidden ? { hidden: true } : {})}
      >
        {inRouter && tableReplaced && <ClearKwViewParams />}
        {header?.({ corpus, hasResults })}

        {/* The one search card; every row carries its own type (Label or a
            drug field). */}
        <KeywordSearchForm
          key={`${corpus}:${formKey}`}
          corpus={corpus}
          sectionOptions={sectionOptions}
          isLoading={query.isFetching}
          onSearch={runSearch}
          initialGroups={restoredSearchParams?.groups}
          initialSources={restoredSearchParams?.sources}
          selectedRegions={filters.regions}
          originScope={versionScope === 'origin'}
          marketingCategories={filters.marketingCategories}
          filtersSlot={filterBar}
          scopeDropdown={(ctx) => (
            <SearchScopeDropdown
              scope={ctx.rowType ?? (ctx.labelOnly || !ctx.fieldTypesEnabled ? 'label' : 'all')}
              onScopeChange={(next) => (next === 'all' || next === 'label'
                ? ctx.onRowTypeChange(null, next === 'label')
                : ctx.onRowTypeChange(next))}
              kindsEnabled={ctx.fieldTypesEnabled}
              sectionOptions={ctx.sectionOptions}
              section={ctx.section}
              onSectionChange={ctx.rowType ? undefined : ctx.onSectionChange}
              sectionDisabled={ctx.sectionDisabled}
              sectionDisabledTitle={ctx.sectionDisabledTitle}
              disabled={ctx.disabled}
            />
          )}
          suggest={isSearchSuggestBarEnabled() ? { regions: filters.regions } : undefined}
          initialRowType={initialRowType}
        />

        {query.data?.kind === 'letters' ? (
          /* Dispatch on response.kind: letters (FDA-letter corpus) →
             LetterResultsTable; drugs (legacy default) → KeywordSearchResults. */
          <LetterResultsTable results={query.data.results} />
        ) : (
          <KeywordSearchResults
            usFdaVisible={usFdaVisible}
            response={query.data ?? null}
            isLoading={query.isFetching}
            currentPage={page}
            pageSize={KW_PAGE_SIZE}
            exactPhrase={exact}
            onPageChange={changePage}
            serverSort={serverSort}
            onServerSortChange={changeSort}
            onPassagesUnavailable={() => { void query.refetch(); }}
            kwRequest={request}
            selectionResetKey={`${selectionEpoch}|${request ? kwSearchIdentity(request) : ''}|${page}|${serverSort ? serializeSortParam(serverSort) : ''}`}
            {...(last ? {
              // Field-row values (Manufacturer: pfizer) are filters, never
              // highlight terms (spec 2026-10-24 §6.3).
              searchKeywords: keywordSearchTerms(last.groups.map((g) => ({ ...g, rows: g.rows.filter((r) => !r.drug_field) }))).join(' '),
            } : {})}
            {...(last ? {
              searchTerms: [
                ...last.groups.flatMap((g) =>
                  g.rows.filter((r) => r.keyword && !r.drug_field)
                    .map((r) => [r.keyword, ...(r.custom_synonyms ?? [])])),
                ...proximityOperands(last.groups).map((t) => [t]),
              ],
            } : {})}
          />
        )}

        {/* Admin only (docSources): the "Unlinked documents" group of an
            engine search. Never for non-admins or the CEPI workspace. */}
        {docSel && request?.document_engine && query.data?.kind === 'drugs' && (
          <UnlinkedDocumentsSection
            group={query.data.unlinked_documents}
            hiddenByFilters={
              (request.sources ?? []).some((s) => s === 'fda_letter')
              && !!(request.drug_name || request.manufacturer
                    || (request.pharmacological_class ?? []).length > 0)
            }
            onLoadMore={async (offset) => {
              const res = await authApi.keywordSearch({
                ...request, passages: 'on_demand', unlinked_offset: offset,
              } as unknown as Record<string, unknown>) as KeywordSearchResponse;
              return res.unlinked_documents;
            }}
          />
        )}

        {query.error && (
          // A 400 is the server refusing this query (e.g. a rule the form
          // could not check): calm guidance, not an alarm. Everything else
          // (5xx, network) is an unexpected failure: the red banner.
          (query.error as { response?: { status?: number } })?.response?.status === 400 ? (
            <div className="p-4 bg-warn-bg border border-warn-line rounded text-warn-ink" role="status">
              <p className="font-medium">Check your search</p>
              <p className="text-sm mt-1">{formatKeywordSearchError(query.error)}</p>
            </div>
          ) : (
            <div className="p-4 bg-white border border-signal rounded text-signal">
              <p className="font-medium">Search Error</p>
              <p className="text-sm mt-1">{formatKeywordSearchError(query.error)}</p>
            </div>
          )
        )}

        {footer}
      </div>
    );
  },
);

export default KeywordSurface;
